import { useSnackbar } from 'notistack';
import { useCallback, useContext, useMemo, useState } from 'react';
import { useLocation, useParams } from 'react-router';
import foundersAPI, { FounderId } from '../../api/founders';
import { SnackMessage } from '../../components/common';
import FounderDetailsForm, {
  ParsedFormValues,
} from '../../components/forms/founder-details';
import { FounderContext } from '../../contexts/founder-context';
import { massAsyncRequest } from '../../utils/api';
import { formatDateToRFC } from '../../utils/date';
import { PastStringDate } from '../../utils/date/PastStringDate';

function FounderDetailsPage() {
  const { founderId } = useParams<{ founderId: FounderId }>();
  const location = useLocation();
  const { enqueueSnackbar } = useSnackbar();

  const {
    founder,
    founderSpecializations,
    updateFounder,
    updateSpecializations,
  } = useContext(FounderContext);

  const [isUpdating, setUpdating] = useState(false);

  const isExternalPage = useMemo(
    () => location.pathname.includes('external'),
    [location],
  );

  const handleSubmitForm = useCallback(
    async (parsedValues: ParsedFormValues) => {
      try {
        const { logo, foundersAdditionalInfo, ...values } = parsedValues.values;
        setUpdating(true);
        let updatedLogo = logo;

        if (parsedValues.logoFile) {
          const updateLogoResult = await foundersAPI.updateAvatar(
            founderId,
            parsedValues.logoFile,
          );
          updatedLogo = updateLogoResult.logo;
        }
        const updatedFounder = await foundersAPI.update(founderId, {
          ...founder,
          ...values,
          logo: updatedLogo,
          foundersAdditionalInfo:
            founder?.foundersAdditionalInfo &&
            founder?.foundersAdditionalInfo.founderId
              ? {
                  ...founder.foundersAdditionalInfo,
                  ...foundersAdditionalInfo,
                }
              : {
                  ...foundersAdditionalInfo,
                  creationDate: formatDateToRFC(new Date()) as PastStringDate,
                },
        });

        const loadedSpecializationsIds = founderSpecializations || [];
        const addedSpecializationsIds = parsedValues.specializations.filter(
          (specializationId) =>
            !loadedSpecializationsIds.includes(specializationId),
        );
        const removedSpecializationsIds = loadedSpecializationsIds.filter(
          (specializationId) =>
            !parsedValues.specializations.includes(specializationId),
        );

        const addSpecializationsRequests = addedSpecializationsIds.map(
          (specializationId) => () =>
            foundersAPI.setFounderSpecialization(founderId, specializationId),
        );
        const removeSpecializationsRequests = removedSpecializationsIds.map(
          (specializationId) => () =>
            foundersAPI.deleteFounderSpecialization(
              founderId,
              specializationId,
            ),
        );
        await Promise.all([
          massAsyncRequest(addSpecializationsRequests),
          massAsyncRequest(removeSpecializationsRequests),
        ]);
        setUpdating(false);

        updateFounder(updatedFounder);
        updateSpecializations(parsedValues.specializations);
        enqueueSnackbar('The founder was successfully updated and saved.', {
          variant: 'success',
        });
      } catch (e: any) {
        const messageError = e.response?.data?.message;

        enqueueSnackbar(
          'An error occurred while updating the founder. Please, try again.',
          {
            content: (key, message) =>
              SnackMessage({
                key,
                message,
                variant: 'error',
                additionalMessage: messageError,
              }),
            variant: 'error',
          },
        );
        setUpdating(false);
      }
    },
    [
      founderId,
      founder,
      founderSpecializations,
      updateFounder,
      updateSpecializations,
      enqueueSnackbar,
    ],
  );

  return (
    <div>
      <FounderDetailsForm
        founderDetails={founder}
        founderSpecializations={founderSpecializations}
        loading={isUpdating}
        onSubmit={handleSubmitForm}
        isExternal={isExternalPage}
      />
    </div>
  );
}

export default FounderDetailsPage;
